import {
  useEffect, useMemo, useState, useCallback,
} from 'react';
import {
  useNavigate, NavigateFunction, useSearchParams,
  useLocation,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroll-component';
import styled from 'styled-components';
import classNames from 'classnames';

import { actions } from 'Slice/AdvanceSearchSlice';
import { actions as flurryActions } from 'Slice/HelperSlice';
import {
  selectAdvanceSearchLoading,
  selectAdvanceSearchLoaded,
  selectAdvanceSearchIsPagination,
  selectAdvanceSearchPersonExtendedLoading,
  selectAdvanceSearchPersonExtendedLoaded,
  selectAdvanceSearchPeopleSection,
  selectAdvanceSearchCompaniesSection,
  selectAdvanceSearchBuyerGroupsSection,
  selectAdvanceSearchParams,
  selectAdvanceSearchMetadata,
  selectAdvanceSearchLinkedinCount,
} from 'Selector/AdvanceSearchSelector';
import { selectViewTab } from 'Selector/UISelector';
import { selectShowGlobalGilroy } from 'Selector/GlobalSearchContentSelector';
import { selectSelectedCompanies, selectSelectedPeople } from 'Selector/GlobalSearchResultsSelector';
import { selectGroupDrawerOpen } from 'Selector/GroupsSelector';
import { actions as globalSearchActions } from 'Slice/GlobalSearchResultsSlice';
import { selectClusterDrawerOpen } from 'Selector/ClustersSelector';
import { actions as clusterActions } from 'Slice/ClustersSlice';
import { AdvanceSearchType, AdvanceSearchRequest, GetAdvancedSearchDistincts } from 'Types/AdvanceSearchResultsTypes';

import AccountPopOver from 'Components/common/components/AccountPopOver';
import SubNavBarPrimary from 'Components/layout/headers/SubNavBarPrimary';
import ViewTabs from 'Components/common/tabs/ViewTabs';
import SearchNoResultFoundWidget from 'Components/common/components/SearchNoResultFoundWidget';
import SelectionActionFooter from 'Components/common/components/SelectionActionFooter';
import DrawerAccountCluster from 'Components/company/components/DrawerAccountCluster';
import PeopleSection from 'Components/common/components/PeopleSection';
import PeopleSectionListView from 'Components/common/components/PeopleSectionListView';
import CompaniesSection from 'Components/common/components/CompaniesSection';
import CompaniesSectionListView from 'Components/common/components/CompaniesSectionListView';
import BuyerGroupsSection from 'Components/common/components/BuyerGroupsSection';
import BuyerGroupsSectionListView from 'Components/common/components/BuyerGroupsSectionListView';
import DrawerAlerts from 'Components/alerts/components/DrawerAlerts';
import DrawerPeopleGroup from 'Components/people/components/DrawerPeopleGroup';
import { actions as groupActions } from 'Slice/GroupsSlice';

import NavBarPrimary from 'Components/layout/headers/NavBarPrimary';
import BackIcon from 'Components/common/assets/svgIcons/BackIcon';
import GlobalSearch from 'Components/globalSearch/components/GlobalSearch';
import { Gilroy } from 'Components/gilroy/LazyGilroy';
import { useGilroy } from 'Utils/hooks/useGilroy';
import { IsTablet, valueToObject, isEnterpriseUser } from 'Utils/UtilityFunctions';
import { CompanyFiltersType, PeopleFilterSelections, ViewInterface } from 'Types/GlobalTypes';
import { ADVANCED_FILTER_PARAMS, FEED_GILROY_ROUTE, SEARCH_PATHNAME_PREFIX } from 'Utils/Constants';
import {
  maxTablet,
  maxWidth,
  primaryFont,
  retina,
  SecondaryColor,
  wMedium,
  smMobile,
} from 'Utils/utilsStyle';
import DrawerPersonas from '../../Components/people/components/DrawerPersonas';
import PeopleFilters from '../../Components/common/components/PeopleFilters';
import CompanyFilters from '../../Components/common/components/CompanyFilters';
import CustomSort, { SortOrder } from '../../Components/common/components/CustomSort';
import { actions as filterActions } from '../../Slice/FilterSlice';
import { selectAdvancedDistinctCompany, selectAdvancedDistinctCompanyCount, selectAdvancedDistinctCompanyLoading, selectAdvancedDistinctCompanyPage, selectAdvancedDistinctCompanySearchTerm, selectAdvancedDistinctDesignation, selectAdvancedDistinctDesignationCount, selectAdvancedDistinctDesignationLoading, selectAdvancedDistinctDesignationPage, selectAdvancedDistinctDesignationSearchTerm } from '../../Selector/FilterSelector';

const parseCompanyFilter = (value: string | null, allOptions: string[]) => {
  if (!value) {
    return undefined;
  }
  if (value === 'selectAll') {
    return allOptions.length ? allOptions : undefined;
  }
  try {
    const parsedValue: unknown = JSON.parse(value);
    return Array.isArray(parsedValue)
      ? parsedValue.filter((option): option is string => typeof option === 'string')
      : undefined;
  } catch {
    return undefined;
  }
};

const parseFormFilter = (value: string | null, allOptions: string[]) => {
  if (!value) return undefined;
  return allOptions.includes(value) ? [value] : undefined;
};

const parsePeopleFilter = (value: string | null, allOptions: string[]) => (
  parseCompanyFilter(value, allOptions)
);

const Wrapper = styled.div`
  .M-Buyer,
  .M-Buyer .infinite-scroll-component {
    overflow-x: hidden !important;
  }
  .M-Buyer::-webkit-scrollbar,
  .M-Buyer .infinite-scroll-component::-webkit-scrollbar {
    height: 0px;
  }
  .M-Buyer {
    scrollbar-width: none;
  }
  .container {
    margin-bottom: 100px;
    @media all and (max-width: ${maxTablet}) {
      margin-bottom: 0px;
    }
  .Grid-width-row.gridUpdated {
    padding-top: 14px;
    @media all and (max-width: ${maxTablet}) {
      padding-top: 0px;
    }
    }
  }
  .heading-inner-page .count {
    color: ${SecondaryColor};
    font-family: ${primaryFont};
    font-weight: ${wMedium};
    font-size: 18px;
    @media all and (max-width: ${maxTablet}) {
      font-size: 16px;
    }
  }
  .person-item {
    margin-bottom: 0px;
  }
  .small-button-chinese {
    margin-bottom: 0px;
  }
  .M-companies .companies-margin {
    .ant-row {
      &.Grid-width-row {
        &.col-six-fix-md {
          margin-top: 0px;
        }
      }
    }
  }
  .company-image-parent {
    @media all and (max-width: ${smMobile}) {
      margin-bottom: 4px;
    }
  }
  .searchHeadings {
    display: none;
  }
  .search-heading.sub-header-main-wrapper .sub-header-wrapper {
    padding: 14px 0 0 0;
  }
  .right-side-sub-header {
    .advance-search-sort {
      margin-right: 10px;
    }
    .view-tabs {
      height: 38px;
      .tabs-wrapper {
        height: 38px;
        .tabs-item {
          height: 38px;
          padding: 4px 10px;
        }
      }
    }
    @media all and (max-width: ${maxWidth}) , ${retina} {
      .advance-search-sort button,
      .people-filter button,
      .company-filter button {
        height: 30px;
        min-height: 30px;
        padding: 4px 12px;
      }
      .date-filter-btn {
        height: 30px;
        min-height: 30px;
        padding-top: 4px;
        padding-bottom: 4px;
      }
      .view-tabs {
        height: 30px;
        .tabs-wrapper {
          height: 30px;
          .tabs-item {
            height: 30px;
            min-height: 30px;
            width: 34px;
            min-width: 34px;
            padding: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            svg {
              height: 14px;
              width: auto;
              min-width: 0;
            }
          }
        }
      }
    }
    @media all and (max-width: ${maxTablet}) {
      .advance-search-sort button,
      .people-filter button,
      .company-filter button,
      .date-filter-btn {
        height: 26px !important;
        min-height: 26px;
      }
      .view-tabs {
        height: 26px;
        .tabs-wrapper {
          height: 26px;
          .tabs-item {
            height: 26px;
            min-height: 26px;
          }
        }
      }
    }
  }
`;

const host = window.location.href;

const searchTypeToTabLabel: Record<string, string> = {
  person: 'People',
  company: 'Company',
  brm: 'Buyer Group',
};

const SIGNATURE_FIELDS: (keyof AdvanceSearchRequest)[] = [
  'searchType', 'fullName', 'designation', 'currentCompany', 'pastCompany',
  'name', 'industry', 'revenue', 'size', 'country', 'region',
  'industries', 'revenues', 'regions', 'companySizes',
  'jobTitles', 'jobLevels', 'personalityTypes', 'filteredCompanies', 'filteredRegions',
];

const getRequestSignature = (req?: AdvanceSearchRequest | null): string => {
  if (!req?.searchType) return '';
  return SIGNATURE_FIELDS.map((field) => `${field}=${req[field] ?? ''}`).join('&');
};

const getAdvDistinctsPayload = (
  type: 'companies' | 'designations',
  form?: AdvanceSearchRequest | null,
  filters?: PeopleFilterSelections,
  extras?: { search?: string; page?: number },
): GetAdvancedSearchDistincts => ({
  type,
  page: extras?.page ?? 1,
  ...(extras?.search && { search: extras.search }),
  ...(form?.fullName && { fullName: form.fullName }),
  ...(form?.designation && { designation: form.designation }),
  ...(form?.currentCompany && { currentCompany: form.currentCompany }),
  ...(form?.pastCompany && { pastCompany: form.pastCompany }),
  ...(form?.country && { country: form.country }),
  ...(form?.region && { region: form.region }),
});

const AdvanceSearchResults = function AdvanceSearchResults() {
  const dispatch = useDispatch();
  const navigate: NavigateFunction = useNavigate();
  const { gilroyStates, gilroyActions } = useGilroy();
  const isEnterprise = isEnterpriseUser();
  const [searchParams] = useSearchParams();
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [createFromSelection, setCreateFromSelection] = useState(false);
  const isTablet = IsTablet();
  const enableBackButton = true;

  const handleBackClick = () => {
    navigate(-1);
  };

  const filters = useSelector(selectAdvanceSearchMetadata);
  const { pathname } = useLocation();
  const advDistinctCompanyFilters = useSelector(selectAdvancedDistinctCompany);
  const advDistinctCompanyPage = useSelector(selectAdvancedDistinctCompanyPage);
  const advDistinctCompanyCount = useSelector(selectAdvancedDistinctCompanyCount);
  const advDistinctCompanyLoading = useSelector(selectAdvancedDistinctCompanyLoading);
  const advDistinctCompanySearchTerm = useSelector(selectAdvancedDistinctCompanySearchTerm);
  const advDistinctDesignationFilters = useSelector(selectAdvancedDistinctDesignation);
  const advDistinctDesignationPage = useSelector(selectAdvancedDistinctDesignationPage);
  const advDistinctDesignationCount = useSelector(selectAdvancedDistinctDesignationCount);
  const advDistinctDesignationLoading = useSelector(selectAdvancedDistinctDesignationLoading);
  const advDistinctDesignationSearchTerm = useSelector(selectAdvancedDistinctDesignationSearchTerm);
  const advanceSearchPeopleLinkedinCount = useSelector(selectAdvanceSearchLinkedinCount);
  const companyFiltersFromUrl = useMemo<CompanyFiltersType>(() => {
    const nextFilters: CompanyFiltersType = {};
    const industryOptions = filters?.industries?.map((option) => option.name) ?? [];
    const revenueOptions = filters?.companyRevenue ?? [];
    const regionOptions = filters?.regions ?? [];
    const sizeOptions = filters?.companySizes ?? [];

    const industry = parseCompanyFilter(searchParams.get('industryF'), industryOptions)
      ?? parseFormFilter(searchParams.get('industry'), industryOptions);
    const revenue = parseCompanyFilter(searchParams.get('revenueF'), revenueOptions)
      ?? parseFormFilter(searchParams.get('revenue'), revenueOptions);
    const region = parseCompanyFilter(searchParams.get('regionF'), regionOptions)
      ?? parseFormFilter(searchParams.get('region'), regionOptions);
    const companySize = parseCompanyFilter(searchParams.get('sizeF'), sizeOptions)
      ?? parseFormFilter(searchParams.get('size'), sizeOptions);

    if (industry) nextFilters.industry = industry;
    if (revenue) nextFilters.revenue = revenue;
    if (region) nextFilters.region = region;
    if (companySize) nextFilters.companySize = companySize;
    return nextFilters;
  }, [
    searchParams,
    filters?.industries,
    filters?.companyRevenue,
    filters?.regions,
    filters?.companySizes,
  ]);

  const peopleFiltersFromUrl = useMemo<PeopleFilterSelections>(() => {
    const nextFilters: PeopleFilterSelections = {};
    const jobTitleOptions = advDistinctDesignationFilters;
    const jobLevelOptions = filters?.jobLevels ?? [];
    const personalityOptions = filters?.personalityTypes?.map((option) => option.filterName) ?? [];
    const companyOptions = advDistinctCompanyFilters.map((option) => option.companyName);
    const regionOptions = filters?.regions ?? [];

    const jobTitle = parsePeopleFilter(searchParams.get('jobTitleF'), jobTitleOptions);
    const jobLevel = parsePeopleFilter(searchParams.get('jobLevelF'), jobLevelOptions);
    const personality = parsePeopleFilter(searchParams.get('personalityF'), personalityOptions);
    const company = parsePeopleFilter(searchParams.get('companyF'), companyOptions);
    const region = parsePeopleFilter(searchParams.get('regionF'), regionOptions)
      ?? parseFormFilter(searchParams.get('region'), regionOptions);

    if (jobTitle) nextFilters.jobTitle = jobTitle;
    if (jobLevel) nextFilters.jobLevel = jobLevel;
    if (personality) nextFilters.personality = personality;
    if (company) nextFilters.company = company;
    if (region) nextFilters.region = region;
    return nextFilters;
  }, [
    searchParams,
    advDistinctDesignationFilters,
    advDistinctCompanyFilters,
    filters?.jobLevels,
    filters?.personalityTypes,
    filters?.regions,
  ]);

  const navRequest = useMemo((): AdvanceSearchRequest | undefined => {
    const searchType = searchParams.get('searchType') as AdvanceSearchType | null;
    if (!searchType) return undefined;
    const req: AdvanceSearchRequest = { searchType };
    const fields = [
      'fullName', 'designation', 'currentCompany', 'pastCompany',
      'name', 'industry', 'revenue', 'size', 'country', 'region',
      'member', 'sharedBy',
    ] as const;
    fields.forEach((field) => {
      const val = searchParams.get(field);
      if (val) (req as unknown as Record<string, string>)[field] = val;
    });
    const sort = searchParams.get('sort');
    if (sort === 'asc' || sort === 'desc') req.sortOrder = sort;
    if (searchType === 'company') {
      if (searchParams.get('industryF') && companyFiltersFromUrl.industry) {
        req.industries = companyFiltersFromUrl.industry;
      }
      if (searchParams.get('revenueF') && companyFiltersFromUrl.revenue) {
        req.revenues = companyFiltersFromUrl.revenue;
      }
      if (searchParams.get('regionF') && companyFiltersFromUrl.region) {
        req.regions = companyFiltersFromUrl.region;
      }
      if (searchParams.get('sizeF') && companyFiltersFromUrl.companySize) {
        req.companySizes = companyFiltersFromUrl.companySize;
      }
    }
    if (searchType === 'person') {
      if (searchParams.get('jobTitleF') && peopleFiltersFromUrl.jobTitle) {
        req.jobTitles = peopleFiltersFromUrl.jobTitle;
      }
      if (searchParams.get('jobLevelF') && peopleFiltersFromUrl.jobLevel) {
        req.jobLevels = peopleFiltersFromUrl.jobLevel.join(',')
      };
      if (searchParams.get('personalityF') && peopleFiltersFromUrl.personality) {
        req.personalityTypes = peopleFiltersFromUrl.personality.join(',');
      }
      if (searchParams.get('companyF') && peopleFiltersFromUrl.company) {
        req.filteredCompanies = peopleFiltersFromUrl.company;
      }
      if (searchParams.get('regionF') && peopleFiltersFromUrl.region) {
        req.filteredRegions = peopleFiltersFromUrl.region;
      }
      if (Object.keys(peopleFiltersFromUrl).length > 0) {
        req.filterApplied = true;
      }
    }
    return req;
  }, [companyFiltersFromUrl, peopleFiltersFromUrl, searchParams]);

  const isLoading = useSelector(selectAdvanceSearchLoading);
  const isLoaded = useSelector(selectAdvanceSearchLoaded);
  const isPaginating = useSelector(selectAdvanceSearchIsPagination);
  const isPersonExtendedLoading = useSelector(selectAdvanceSearchPersonExtendedLoading);
  const isPersonExtendedLoaded = useSelector(selectAdvanceSearchPersonExtendedLoaded);
  const peopleSection = useSelector(selectAdvanceSearchPeopleSection);
  const companiesSection = useSelector(selectAdvanceSearchCompaniesSection);
  const buyerGroupsSection = useSelector(selectAdvanceSearchBuyerGroupsSection);
  const storedParams = useSelector(selectAdvanceSearchParams);
  const tabView: ViewInterface = useSelector(selectViewTab);
  const isListView = tabView.view === 'listView';
  const showGlobalGilroy = useSelector(selectShowGlobalGilroy);
  const selectedCompanies = useSelector(selectSelectedCompanies);
  const selectedPeople = useSelector(selectSelectedPeople);
  const clusterDrawerOpen = useSelector(selectClusterDrawerOpen);
  const groupDrawerOpen = useSelector(selectGroupDrawerOpen);
  const sortOrder = (searchParams.get('sort') as SortOrder | null) ?? null;
  const activeSearchType: AdvanceSearchType | '' = storedParams?.searchType ?? '';

  const hasMoreAdvDistinctCompanies = advDistinctCompanyFilters.length < advDistinctCompanyCount;
  const hasMoreAdvDistinctDesignations = advDistinctDesignationFilters.length < advDistinctDesignationCount;

  const toggleDrawer = () => {
    dispatch(clusterActions.setDrawerOpen(!clusterDrawerOpen));
  };

  const handleCreateAccountCluster = () => {
    dispatch(clusterActions.setDrawerOpen(true));
  };

  const handleAddToBuyerGroup = () => {
    setCreateFromSelection(true);
    setOpenCreateModal(true);
    setShowNew(true);
  };

  const handleSortChange = (order: SortOrder | null) => {
    const params = new URLSearchParams(searchParams);
    if (order) {
      params.set('sort', order);
    } else {
      params.delete('sort');
    }
    navigate(`?${params.toString()}`);

    if (storedParams) {
      const nextParams: AdvanceSearchRequest = {
        ...storedParams,
        page: 1,
        ...(peopleFiltersFromUrl.jobTitle && { jobTitles: peopleFiltersFromUrl.jobTitle }),
        ...(peopleFiltersFromUrl.jobLevel && { jobLevels: peopleFiltersFromUrl.jobLevel.join(',') }),
        ...(peopleFiltersFromUrl.personality && { personalityTypes: peopleFiltersFromUrl.personality.join(',') }),
        ...(peopleFiltersFromUrl.company && { companies: peopleFiltersFromUrl.company }),
        ...(peopleFiltersFromUrl.region && { regions: peopleFiltersFromUrl.region }),
        ...(companyFiltersFromUrl.industry && { industries: companyFiltersFromUrl.industry }),
        ...(companyFiltersFromUrl.region && { regions: companyFiltersFromUrl.region }),
        ...(companyFiltersFromUrl.revenue && { revenues: companyFiltersFromUrl.revenue }),
        ...(companyFiltersFromUrl.companySize && { companySizes: companyFiltersFromUrl.companySize }),
      };
      if (order) {
        nextParams.sortOrder = order;
      } else {
        delete nextParams.sortOrder;
      }
      dispatch(actions.triggerAdvanceSearch(nextParams));
    }
  };

  useEffect(() => () => {
    if (!window.location.pathname.startsWith(SEARCH_PATHNAME_PREFIX)) {
      dispatch(globalSearchActions.clearAllSearchSelections());
    }
  }, []);

  useEffect(() => {
    dispatch(clusterActions.resetClusterDetails());
  }, [dispatch])

  useEffect(() => {
    const node = document.querySelector('body');
    return (clusterDrawerOpen || groupDrawerOpen
      || selectedCompanies.length > 0 || selectedPeople.length > 0)
      ? node?.classList.add('body-drawer-toggle') : node?.classList.remove('body-drawer-toggle');
  }, [clusterDrawerOpen, groupDrawerOpen, selectedCompanies, selectedPeople]);

  const page = storedParams?.page ?? 1;

  const toggle = useCallback((flag: boolean) => {
    setOpenCreateModal(flag);
    if (!flag) setCreateFromSelection(false);
  }, []);

  const isDrag = useCallback((a: boolean) => {
    if (a) dispatch(groupActions.setDropComplete(false));
    setDragging(a);
  }, [dispatch]);

  useEffect(() => {
    setShowNew(false);
    setOpenCreateModal(dragging);
    setCreateFromSelection(false);
  }, [dragging]);

  useEffect(() => {
    if (!navRequest) {
      if (!storedParams) navigate('/feed', { replace: true });
      return;
    }

    if (isLoading) return;

    if (storedParams && getRequestSignature(navRequest) === getRequestSignature(storedParams)) {
      return;
    }

    if (navRequest.searchType === 'brm' && !navRequest.name && !navRequest.size) {
      navigate(`${FEED_GILROY_ROUTE}?notice=search-cleared`, { replace: true });
      return;
    }
    const isBrm = navRequest.searchType === 'brm';
    const requestToDispatch = isBrm
      ? { ...navRequest, member: undefined, sharedBy: undefined }
      : navRequest;
    dispatch(actions.triggerAdvanceSearch(requestToDispatch));
    if (navRequest.searchType === 'person') {
      dispatch(actions.setAdvanceSearchPeopleForm({
        fullName: navRequest.fullName ?? '',
        currentCo: navRequest.currentCompany ?? '',
        previousCo: navRequest.pastCompany ?? '',
        designation: navRequest.designation ?? '',
        country: navRequest.country ?? '',
        region: navRequest.region ?? '',
      }));
    } else if (navRequest.searchType === 'company') {
      dispatch(actions.setAdvanceSearchCompanyForm({
        name: navRequest.name ?? '',
        industry: navRequest.industry ?? '',
        revenue: navRequest.revenue ?? '',
        country: navRequest.country ?? '',
        region: navRequest.region ?? '',
        size: navRequest.size ?? '',
      }));
    } else if (navRequest.searchType === 'brm') {
      dispatch(actions.setAdvanceSearchBrmForm({
        name: navRequest.name ?? '',
        member: 0,
        size: navRequest.size ?? '',
        sharedBy: 0,
      }));
      const cleanParams = new URLSearchParams(searchParams);
      cleanParams.delete('member');
      cleanParams.delete('sharedBy');
      navigate(`?${cleanParams.toString()}`, { replace: true });
    }
  }, [navRequest, storedParams, isLoading, dispatch, navigate, searchParams]);

  // Derived display values
  const persons = peopleSection?.persons ?? [];
  const companies = companiesSection?.companies ?? [];
  const buyerGroups = buyerGroupsSection?.groups ?? [];

  const totalPersonCount = peopleSection?.count ?? 0;
  const totalCompanyCount = companiesSection?.count ?? 0;
  const totalBuyerGroupCount = buyerGroupsSection?.count ?? 0;


  const isInitialPeopleLoading = !isLoaded && persons.length === 0;
  const isInitialCompanyLoading = !isLoaded && companies.length === 0;
  const isInitialBuyerGroupsLoading = !isLoaded && buyerGroups.length === 0;

  const hasMorePeople = persons.length < totalPersonCount;
  const hasMoreCompanies = companies.length < totalCompanyCount;
  const hasMoreBuyerGroups = buyerGroups.length < totalBuyerGroupCount;

  const personsAll = persons;
  const companiesAll = companies;
  const buyerGroupsAll = buyerGroups;

  const fetchMorePeople = () => {
    if (!isLoading && storedParams) {
      dispatch(actions.triggerAdvanceSearch({ ...storedParams, page: page + 1 }));
    }
  };

  const fetchMoreCompanies = () => {
    if (!isLoading && storedParams) {
      dispatch(actions.triggerAdvanceSearch({ ...storedParams, page: page + 1 }));
    }
  };

  const fetchMoreBuyerGroups = () => {
    if (!isLoading && storedParams) {
      dispatch(actions.triggerAdvanceSearch({ ...storedParams, page: page + 1 }));
    }
  };

  const allSearchesSettled = activeSearchType === 'person'
    ? isLoaded && isPersonExtendedLoaded
    : isLoaded;

  const showNoResults = allSearchesSettled
    && !isLoading
    && persons.length === 0
    && companies.length === 0
    && buyerGroups.length === 0;

  useEffect(() => {
    if (showNoResults) {
      const searchFields = Object.fromEntries(
        Object.entries(storedParams ?? {}).filter(
          ([key, value]) => key !== 'searchType' && key !== 'page' && String(value).trim() !== '',
        ),
      );
      dispatch(flurryActions.callFlurryEvent(
        valueToObject('no_result_searches', 0, {
          selected_tab: searchTypeToTabLabel[activeSearchType] ?? activeSearchType,
          field_value: JSON.stringify(searchFields),
          host,
        }),
      ));
    }
  }, [showNoResults]);

  const hasAppliedFilters = ADVANCED_FILTER_PARAMS.some((field) => {
    const value = storedParams?.[field];

    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return typeof value === 'string' && value.trim().length > 0;
  });

  const isPeopleFilterApplied = ['jobTitleF', 'jobLevelF', 'personalityF', 'companyF', 'regionF']
    .some((param) => Boolean(searchParams.get(param)?.trim()));
  const isCompanyFilterApplied = ['industryF', 'revenueF', 'regionF', 'sizeF']
    .some((param) => Boolean(searchParams.get(param)?.trim()));

  const isAllSelected = (selected: string[], allOptions: string[]) => {
    return (
      selected.length === allOptions.length &&
      selected.every((item) => allOptions.includes(item))
    );
  }
  const handleCompanyFiltersApply = (companyFilters: CompanyFiltersType) => {
    const params = new URLSearchParams(searchParams);
    const filterConfig = [
      ['industry', 'industryF', filters?.industries?.map((option) => option.name) ?? []],
      ['revenue', 'revenueF', filters?.companyRevenue ?? []],
      ['region', 'regionF', filters?.regions ?? []],
      ['companySize', 'sizeF', filters?.companySizes ?? []],
    ] as const;

    filterConfig.forEach(([category, urlKey, allOptions]) => {
      const selected = companyFilters[category] ?? [];
      params.delete(urlKey);
      if (!selected.length) {
        // params.delete(urlKey);
        return;
      }
      if (isAllSelected(selected, allOptions)) {
        params.set(urlKey, 'selectAll');
      } else {
        params.set(urlKey, JSON.stringify(selected));
      }
    });
    params.set('filterApplied', 'true');
    navigate(`${pathname}?${params.toString()}`);

    if (storedParams) {
      const searchRequest = { ...storedParams, page: 1 };
      delete searchRequest.industries;
      delete searchRequest.revenues;
      delete searchRequest.regions;
      delete searchRequest.companySizes;
      dispatch(actions.triggerAdvanceSearch({
        ...searchRequest,
        ...(companyFilters.industry?.length && { industries: companyFilters.industry }),
        ...(companyFilters.revenue?.length && { revenues: companyFilters.revenue }),
        ...(companyFilters.region?.length && { regions: companyFilters.region }),
        ...(companyFilters.companySize?.length && { companySizes: companyFilters.companySize }),
      }));
    }

  }

  const personFormKey = navRequest?.searchType === 'person'
    ? [
      navRequest.fullName ?? '',
      navRequest.designation ?? '',
      navRequest.currentCompany ?? '',
      navRequest.pastCompany ?? '',
      navRequest.country ?? '',
      navRequest.region ?? '',
    ].join('|')
    : '';

  useEffect(() => {
    if (navRequest?.searchType !== 'person') return;
    dispatch(filterActions.getAdvancedSearchDistincts(
      getAdvDistinctsPayload('companies', navRequest, peopleFiltersFromUrl, {
        search: advDistinctCompanySearchTerm,
      }),
    ));
  }, [advDistinctCompanySearchTerm, personFormKey]);

  useEffect(() => {
    if (navRequest?.searchType !== 'person') return;
    dispatch(filterActions.getAdvancedSearchDistincts(
      getAdvDistinctsPayload('designations', navRequest, peopleFiltersFromUrl, {
        search: advDistinctDesignationSearchTerm,
      }),
    ));
  }, [advDistinctDesignationSearchTerm, personFormKey]);

  const fetchMoreAdvDistinctCompanies = (searchTerm?: string) => {
    dispatch(filterActions.getAdvancedSearchDistincts(
      getAdvDistinctsPayload('companies', navRequest, peopleFiltersFromUrl, {
        search: searchTerm,
        page: advDistinctCompanyPage + 1,
      }),
    ));
  };

  const fetchMoreAdvDistinctDesignation = (searchTerm?: string) => {
    dispatch(filterActions.getAdvancedSearchDistincts(
      getAdvDistinctsPayload('designations', navRequest, peopleFiltersFromUrl, {
        search: searchTerm,
        page: advDistinctDesignationPage + 1,
      }),
    ));
  };

  const handlePeopleFiltersApply = (peopleFilters: PeopleFilterSelections) => {
    const selectedPeopleFilters = {
      jobTitle: peopleFilters.jobTitle ?? [],
      jobLevel: peopleFilters?.jobLevel ?? [],
      personality: peopleFilters?.personality ?? [],
      company: peopleFilters?.company ?? [],
      region: peopleFilters?.region ?? [],
    };
    const params = new URLSearchParams(searchParams);
    const filterConfig = [
      ['jobTitle', 'jobTitleF', advDistinctDesignationFilters],
      ['jobLevel', 'jobLevelF', filters?.jobLevels ?? []],
      ['personality', 'personalityF', filters?.personalityTypes?.map((option) => option.filterName) ?? []],
      ['company', 'companyF', advDistinctCompanyFilters.map((option) => option.companyName)],
      ['region', 'regionF', filters?.regions ?? []],
    ] as const;

    filterConfig.forEach(([category, urlKey, allOptions]) => {
      const selected = selectedPeopleFilters[category];
      params.delete(urlKey);
      if (!selected.length) return;
      const shouldUseSelectAll = category !== 'jobTitle' && category !== 'company';
      params.set(urlKey, shouldUseSelectAll && isAllSelected(selected, allOptions)
        ? 'selectAll'
        : JSON.stringify(selected));
    });
    navigate(`${pathname}?${params.toString()}`);

    if (storedParams) {
      const searchRequest = { ...storedParams, page: 1, filterApplied: true };
      delete searchRequest.jobTitles;
      delete searchRequest.jobLevels;
      delete searchRequest.personalityTypes;
      delete searchRequest.filteredCompanies;
      delete searchRequest.filteredRegions;
      dispatch(actions.triggerAdvanceSearch({
        ...searchRequest,
        ...(selectedPeopleFilters.jobTitle.length && { jobTitles: selectedPeopleFilters.jobTitle }),
        ...(selectedPeopleFilters.jobLevel.length && { jobLevels: selectedPeopleFilters.jobLevel.join(',') }),
        ...(selectedPeopleFilters.personality.length && { personalityTypes: selectedPeopleFilters.personality.join(',') }),
        ...(selectedPeopleFilters.company.length && { filteredCompanies: selectedPeopleFilters.company }),
        ...(selectedPeopleFilters.region.length && { filteredRegions: selectedPeopleFilters.region }),
      }));
      dispatch(filterActions.getAdvancedSearchDistincts(
        getAdvDistinctsPayload('designations', navRequest, selectedPeopleFilters, {
          search: advDistinctDesignationSearchTerm,
        }),
      ));
      dispatch(filterActions.getAdvancedSearchDistincts(
        getAdvDistinctsPayload('companies', navRequest, selectedPeopleFilters, {
          search: advDistinctCompanySearchTerm,
        }),
      ));
    }
  };


  return (
    <Wrapper className='advance-search-wrapper'>
      <NavBarPrimary headerClassName="box-shadow no-overflow-on-header">
        <div className="left-side-wrap">
          {enableBackButton && (
            <BackIcon onClick={handleBackClick} />
          )}
          <h1 className="heading">Results</h1>
        </div>
        <div className="right-side-wrap">
          <GlobalSearch iconMode={isTablet} />
          <DrawerAlerts />
          <AccountPopOver />
        </div>
      </NavBarPrimary>

      <SubNavBarPrimary headerClassName="search-heading">
        <div className="heading-inner-page tabs-placeholder wrap-slider hidden-scroll">
          <div className="tips-placeholder ml-0">
            <div className="count">
              {/* show people count here */}
              {activeSearchType === 'person' ? (
                <span>
                  People ({totalPersonCount})
                </span>
              ) : activeSearchType === 'company' ? (
                <span>
                  Companies ({totalCompanyCount})
                </span>
              ) : activeSearchType === 'brm' ? (
                <span>
                  Buyer Groups ({totalBuyerGroupCount})
                </span>
              ) : (
                ''
              )}
            </div>
          </div>
          <div className="right-side-sub-header">
            {
              (!showNoResults
                || (activeSearchType === 'person' && isPeopleFilterApplied)
                || (activeSearchType === 'company' && isCompanyFilterApplied))
              && advanceSearchPeopleLinkedinCount === 0 && isPersonExtendedLoaded && (
                <div className={classNames('advance-search-sort')}>
                  <CustomSort value={sortOrder} onSortChange={handleSortChange} iconMode={isTablet} disabled={isLoading} />
                </div>
              )
            }
            {
              ((!showNoResults || isPeopleFilterApplied)
                && activeSearchType === 'person'
                && (isPersonExtendedLoaded && advanceSearchPeopleLinkedinCount === 0)) && (
                <div className={classNames('people-filter')}>
                  <PeopleFilters
                    jobLevelData={filters?.jobLevels}
                    personalityData={filters?.personalityTypes}
                    regionData={filters?.regions}
                    designationData={advDistinctDesignationFilters}
                    companyData={advDistinctCompanyFilters}
                    hasMoreCompanies={hasMoreAdvDistinctCompanies}
                    hasMoreDesignations={hasMoreAdvDistinctDesignations}
                    companiesLoading={advDistinctCompanyLoading}
                    designationsLoading={advDistinctDesignationLoading}
                    onFetchMoreCompanies={fetchMoreAdvDistinctCompanies}
                    onFetchMoreDesignations={fetchMoreAdvDistinctDesignation}
                    initialFilters={peopleFiltersFromUrl}
                    onApply={handlePeopleFiltersApply}
                    isAdvanceSearch={true}
                    disabled={isLoading}
                  />
                </div>
              )
            }
            {
              ((!showNoResults || isCompanyFilterApplied) && activeSearchType === 'company') && (
                <div className={classNames('company-filter')}>
                  <CompanyFilters
                    revenueData={filters?.companyRevenue}
                    companySizeData={filters?.companySizes}
                    industryData={filters?.industries}
                    regionData={filters?.regions}
                    initialFilters={companyFiltersFromUrl}
                    onApply={handleCompanyFiltersApply}
                    disabled={isLoading}
                  />
                </div>
              )
            }
            <div className={classNames('view-tabs', { notdisabledTabs: true })}>
              <ViewTabs disabled={false} listView={isListView} />
            </div>
          </div>
        </div>
      </SubNavBarPrimary>

      <div className="container placement-logic">
        <div className="styleing-wrapper fancyBox">
          <DrawerPeopleGroup
            openNewCreate={openCreateModal}
            setOpenNew={toggle}
            showNew={showNew}
            toggleShowNew={(bool: boolean) => setShowNew(bool)}
            noButton
            drag={dragging}
            fromSearchSelection={createFromSelection}
          />
          {isEnterprise && <DrawerPersonas />}

          {activeSearchType === 'person' && (
            <>
              {isListView ? (
                <InfiniteScroll
                  hasMore={hasMorePeople}
                  dataLength={personsAll.length}
                  next={fetchMorePeople}
                  loader={false}
                  className="o-x-hidden o-y-hidden spacer"
                >
                  <PeopleSectionListView
                    heading={peopleSection?.title}
                    peopleCollection={personsAll}
                    loading={isInitialPeopleLoading}
                    peopleCount={persons}
                    count={totalPersonCount}
                    invertFollow
                    inGlobalSearch
                    inAdvanceSearch
                    query=""
                    hideShowAll
                    primarySearchLoaded={isLoaded}
                    extendedSearchLoaded={isPersonExtendedLoaded}
                    isPaginationOverride={isPaginating}
                  />
                </InfiniteScroll>
              ) : (
                <InfiniteScroll
                  hasMore={hasMorePeople}
                  dataLength={personsAll.length}
                  next={fetchMorePeople}
                  loader={false}
                  className="o-x-hidden M-People detailPage"
                >
                  <PeopleSection
                    heading={peopleSection?.title}
                    hideDefaultBackground
                    peopleCollection={personsAll}
                    loading={isInitialPeopleLoading}
                    peopleCount={persons}
                    count={totalPersonCount}
                    query=""
                    inGlobalSearch
                    inAdvanceSearch
                    invertFollow
                    bothSearchLoading={!isPersonExtendedLoaded || !isLoaded}
                    hideShowAll
                    primarySearchLoaded={isLoaded}
                    extendedSearchLoaded={isPersonExtendedLoaded}
                    isPaginationOverride={isPaginating}
                    isDrag={isDrag}
                  />
                </InfiniteScroll>
              )}
            </>
          )}

          {activeSearchType === 'company' && (
            <>
              {isListView ? (
                <InfiniteScroll
                  hasMore={hasMoreCompanies}
                  dataLength={companiesAll.length}
                  next={fetchMoreCompanies}
                  loader={false}
                  className="o-x-hidden M-Companies detailPage spacer"
                >
                  <CompaniesSectionListView
                    heading={companiesSection?.title}
                    companiesCollection={companiesAll}
                    loading={isInitialCompanyLoading}
                    companyCount={companies}
                    count={totalCompanyCount}
                    listView={isListView}
                    inGlobalSearch
                    inAdvanceSearch
                    query=""
                    hideShowAll
                    isPaginationOverride={isPaginating}
                  />
                </InfiniteScroll>
              ) : (
                <InfiniteScroll
                  hasMore={hasMoreCompanies}
                  dataLength={companiesAll.length}
                  next={fetchMoreCompanies}
                  loader={false}
                  className="o-x-hidden M-Companies detailPage spacer"
                >
                  <CompaniesSection
                    heading={companiesSection?.title}
                    hideDefaultBackground
                    companiesCollection={companiesAll}
                    loading={isInitialCompanyLoading}
                    companyCount={companies}
                    count={totalCompanyCount}
                    query=""
                    listView={isListView}
                    inGlobalSearch
                    inAdvanceSearch
                    hideShowAll
                    isPaginationOverride={isPaginating}
                  />
                </InfiniteScroll>
              )}
            </>
          )}

          {activeSearchType === 'brm' && (
            <>
              {isListView ? (
                <InfiniteScroll
                  hasMore={hasMoreBuyerGroups}
                  dataLength={buyerGroupsAll.length}
                  next={fetchMoreBuyerGroups}
                  loader={false}
                  className="o-x-hidden o-y-hidden M-Buyer spacer"
                >
                  <BuyerGroupsSectionListView
                    heading={buyerGroupsSection?.title}
                    buyerGroupsCollection={buyerGroupsAll}
                    loading={isInitialBuyerGroupsLoading}
                    buyerGroupCount={buyerGroups}
                    count={totalBuyerGroupCount}
                    inGlobalSearch
                    query=""
                    isPaginationOverride={isPaginating}
                  />
                </InfiniteScroll>
              ) : (
                <InfiniteScroll
                  hasMore={hasMoreBuyerGroups}
                  dataLength={buyerGroupsAll.length}
                  next={fetchMoreBuyerGroups}
                  loader={false}
                  className="o-x-hidden o-y-hidden M-Buyer spacer"
                >
                  <BuyerGroupsSection
                    heading={buyerGroupsSection?.title}
                    buyerGroupsCollection={buyerGroupsAll}
                    loading={isInitialBuyerGroupsLoading}
                    buyerGroupCount={buyerGroups}
                    count={totalBuyerGroupCount}
                    inGlobalSearch
                    query=""
                    isPaginationOverride={isPaginating}
                  />
                </InfiniteScroll>
              )}
            </>
          )}

          {showNoResults && <SearchNoResultFoundWidget marginTop="250px" hasFilters={hasAppliedFilters} />}

        </div>

        {activeSearchType === 'person' ? (
          <SelectionActionFooter
            selectedCount={selectedPeople.length}
            buttonTitle="Add to Buyer Group"
            itemLabel="person"
            itemLabelPlural="people"
            onButtonClick={handleAddToBuyerGroup}
            onClearSelection={() => dispatch(globalSearchActions.clearSelectedPeople())}
          />
        ) : null}

        {activeSearchType === 'company' ? (
          <>
            <SelectionActionFooter
              selectedCount={selectedCompanies.length}
              buttonTitle="Create Account Cluster"
              itemLabel="company"
              itemLabelPlural="companies"
              onButtonClick={handleCreateAccountCluster}
              onClearSelection={() => dispatch(globalSearchActions.clearSelectedCompanies())}
            />
            <DrawerAccountCluster
              toggleDrawer={toggleDrawer}
              mode="create"
              inGlobalSearch
            />
          </>
        ) : null}
      </div>
      {showGlobalGilroy
        ? (
          <Gilroy gilroyStates={gilroyStates} gilroyActions={gilroyActions} />
        ) : null}
    </Wrapper>
  );
};

export default AdvanceSearchResults;
